import type { ReactElement } from 'react';
import type { MCPServerListItem } from '@kirin/api-client/contracts/mcp';
import { Badge } from '@/components/ui/badge';
import { BlockToggle } from '@/components/shared/BlockToggle';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  isPolicyApplied,
  type BlockConfirmTarget,
} from './gatewayControlUtils';

interface GatewayControlCellProps {
  row: MCPServerListItem;
  appliedPolicyVersion: number | null;
  onAllowServer: (serverId: string) => void;
  onRequestBlockConfirmation: (target: BlockConfirmTarget) => void;
}

/**
 * Why the switch is inert (KIR-5042). There is no gateway policy route on the
 * backend: `routes/organizations/` has no gateway file, and the PRs that add
 * one are still open. The toggle used to write to an in-memory fixture store —
 * it moved on screen and no server ever heard about it, which is a worse lie
 * than a control that says it is not ready.
 */
const UNAVAILABLE_REASON =
  'Blocking gateway servers is not available yet. This shows what the gateway reports; changing it needs a Kirin release that can send the policy.';

/** Last column of the MCP Servers table when gateway control is on (KIR-3940 AC 3). */
export function GatewayControlCell({
  row,
  appliedPolicyVersion,
  onAllowServer,
  onRequestBlockConfirmation,
}: GatewayControlCellProps): ReactElement {
  if (row.provenance !== 'gateway') {
    return (
      <span className="text-sm italic text-muted-foreground">
        Not gateway-managed
      </span>
    );
  }

  const serverId = row.id ?? row.name;
  const blocked = row.blocked ?? false;
  const applied = isPolicyApplied(row.policyVersion, appliedPolicyVersion);

  return (
    <div className="flex items-center gap-3">
      {/* The callbacks stay wired and the `useMutation` shape is untouched, so
          the slice that adds the policy route flips `disabled` and nothing else
          moves. */}
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <BlockToggle
              blocked={blocked}
              name={row.name}
              disabled
              onAllow={() => onAllowServer(serverId)}
              onRequestBlock={() =>
                onRequestBlockConfirmation({
                  kind: 'server',
                  serverId,
                  serverName: row.name,
                  toolCount: row.tools?.length ?? 0,
                })
              }
            />
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          {UNAVAILABLE_REASON}
        </TooltipContent>
      </Tooltip>
      <Badge
        variant="outline"
        className={
          applied
            ? 'text-green-700 border-green-200'
            : 'text-orange-600 border-orange-200'
        }
      >
        {applied ? 'Applied' : 'Pending sync'}
      </Badge>
    </div>
  );
}
